import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Badge from "./ui/Badge.jsx";
import Button from "./ui/Button.jsx";
import {
  getProjectTenantDisplayUrl,
  getProviderBadgeLabel,
  isAuth0Connected,
  projectHeaderLabel,
} from "../utils/projectProvider.js";

const NAME_MAX = 100;

function statusVariant(st) {
  const s = String(st || "").toLowerCase();
  if (s === "active") return "active";
  if (s === "complete" || s === "completed") return "complete";
  if (s === "paused") return "paused";
  if (s === "archived") return "archived";
  return "info";
}

export default function Topbar({
  project,
  userEmail,
  onDisconnect,
  pendingApprovalCount = 0,
  onOpenApprovals,
  onOpenFrameworks,
  onRenamed,
  readOnlyAssessment = false,
}) {
  const navigate = useNavigate();
  const name = projectHeaderLabel(project);
  const displayName = project?.org?.name || project?.orgName || "Project";
  const providerBadge = getProviderBadgeLabel(project);
  const url = getProjectTenantDisplayUrl(project);
  const status = project?.status || "active";
  const projectId = project?.projectId;

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(displayName);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (!editing) setDraft(displayName);
  }, [displayName, editing]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const cancelEdit = () => {
    setEditing(false);
    setDraft(displayName);
    setError("");
  };

  const saveRename = async () => {
    if (!projectId || saving) return;
    const next = String(draft || "").trim();
    if (!next) {
      setError("Name cannot be empty");
      return;
    }
    if (next === displayName) {
      cancelEdit();
      return;
    }
    setSaving(true);
    setError("");
    try {
      const res = await fetch(`/api/projects/${encodeURIComponent(projectId)}/name`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: next.slice(0, NAME_MAX) }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.project) {
        setError(data?.error || "Could not rename");
        return;
      }
      setEditing(false);
      onRenamed?.(data.project);
    } catch {
      setError("Could not rename");
    } finally {
      setSaving(false);
    }
  };

  return (
    <header
      style={{
        height: "48px",
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0 20px",
        background: "var(--bg-surface)",
        borderBottom: "1px solid var(--border)",
        zIndex: 20,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "8px", minWidth: 0, flex: 1 }}>
        <span
          style={{
            fontSize: "11px",
            fontWeight: 700,
            letterSpacing: "0.02em",
            textTransform: "lowercase",
            color: "var(--idaxis-accent-primary)",
            flexShrink: 0,
          }}
        >
          idwiz
        </span>
        <span
          style={{
            width: "1px",
            height: "14px",
            background: "var(--border)",
            flexShrink: 0,
          }}
        />
        {editing ? (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, minWidth: 0 }}>
            <input
              ref={inputRef}
              value={draft}
              maxLength={NAME_MAX}
              disabled={saving}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  void saveRename();
                } else if (e.key === "Escape") {
                  e.preventDefault();
                  cancelEdit();
                }
              }}
              aria-label="Project name"
              style={{
                fontSize: 13,
                fontWeight: 500,
                color: "var(--text-primary)",
                background: "var(--bg-elevated)",
                border: `1px solid ${error ? "var(--critical)" : "var(--border)"}`,
                borderRadius: 6,
                padding: "4px 8px",
                minWidth: 140,
                maxWidth: 240,
              }}
            />
            <button
              type="button"
              onClick={() => void saveRename()}
              disabled={saving}
              style={{
                fontSize: 11,
                fontWeight: 600,
                padding: "4px 8px",
                borderRadius: 6,
                border: "1px solid var(--border)",
                background: "var(--bg-elevated)",
                color: "var(--text-primary)",
                cursor: saving ? "wait" : "pointer",
              }}
            >
              {saving ? "…" : "Save"}
            </button>
            <button
              type="button"
              onClick={cancelEdit}
              disabled={saving}
              style={{
                fontSize: 11,
                fontWeight: 600,
                padding: "4px 8px",
                borderRadius: 6,
                border: "none",
                background: "transparent",
                color: "var(--text-secondary)",
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            {error ? (
              <span style={{ fontSize: 11, color: "var(--critical)", whiteSpace: "nowrap" }}>{error}</span>
            ) : null}
          </span>
        ) : (
          <button
            type="button"
            onClick={() => {
              setDraft(displayName);
              setError("");
              setEditing(true);
            }}
            title={`${name} — click to rename`}
            style={{
              fontSize: "13px",
              fontWeight: 500,
              color: "var(--text-primary)",
              maxWidth: "200px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              background: "none",
              border: "1px solid transparent",
              borderRadius: 6,
              padding: "2px 6px",
              margin: "0 -6px",
              cursor: "text",
              textAlign: "left",
            }}
            className="topbar-project-name"
          >
            {displayName}
          </button>
        )}
        <span
          className="text-dim"
          style={{ fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.06em", flexShrink: 0 }}
        >
          {providerBadge}
        </span>
        <span
          className="font-mono text-secondary"
          style={{
            fontSize: "11px",
            maxWidth: "220px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={url}
        >
          {url || "—"}
        </span>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: "12px", flexShrink: 0 }}>
        <Badge variant={statusVariant(status)}>{status}</Badge>
        {isAuth0Connected(project) ? (
          <span
            className="text-dim"
            style={{ fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.06em" }}
          >
            Connected
          </span>
        ) : null}
        <span
          style={{
            width: "1px",
            height: "14px",
            background: "var(--border)",
          }}
        />
        <button
          type="button"
          onClick={() => navigate("/settings?tab=profile")}
          className="text-dim"
          title="Account settings"
          style={{
            fontSize: "11px",
            maxWidth: "180px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            background: "none",
            border: "none",
            padding: 0,
            cursor: "pointer",
            color: "var(--text-dim)",
          }}
        >
          {userEmail || ""}
        </button>
        {typeof onOpenFrameworks === "function" ? (
          <button
            type="button"
            onClick={onOpenFrameworks}
            style={{
              padding: "6px 12px",
              fontSize: "12px",
              fontWeight: 600,
              borderRadius: "6px",
              border: "1px solid var(--border)",
              background: "var(--bg-elevated)",
              color: "var(--text-primary)",
              cursor: "pointer",
            }}
          >
            Frameworks
          </button>
        ) : null}
        {typeof onOpenApprovals === "function" ? (
          <button
            type="button"
            onClick={onOpenApprovals}
            style={{
              position: "relative",
              padding: "6px 12px",
              fontSize: "12px",
              fontWeight: 600,
              borderRadius: "6px",
              border: "1px solid var(--border)",
              background: "var(--bg-elevated)",
              color: "var(--text-primary)",
              cursor: "pointer",
            }}
          >
            Approvals
            {pendingApprovalCount > 0 ? (
              <span
                style={{
                  position: "absolute",
                  top: "-6px",
                  right: "-6px",
                  minWidth: "18px",
                  height: "18px",
                  padding: "0 5px",
                  borderRadius: "999px",
                  fontSize: "10px",
                  fontWeight: 700,
                  lineHeight: "18px",
                  textAlign: "center",
                  background: "var(--accent)",
                  color: "#0a0f1a",
                }}
              >
                {pendingApprovalCount > 99 ? "99+" : pendingApprovalCount}
              </span>
            ) : null}
          </button>
        ) : null}
        <Button variant="ghost" size="sm" onClick={() => navigate("/")}>
          ← Projects
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onDisconnect}
          className="topbar-disconnect"
          style={{ color: "var(--text-secondary)" }}
        >
          Disconnect
        </Button>
      </div>
      <style>{`
        .topbar-disconnect:hover {
          color: var(--critical) !important;
        }
        .topbar-project-name:hover {
          border-color: var(--border) !important;
          background: var(--bg-elevated) !important;
        }
      `}</style>
    </header>
  );
}
